import { ROLES } from "./constants";

export type Subject = "Video" | "Administration";

export const RoleAbilities = {
  // Viewer
  [ROLES.VIEWER]: (subject: Subject) => {
    switch (subject) {
      case "Video":
        return { read: true };
      default:
        return false;
    }
  },
  // Coordinator
  [ROLES.COORDINATOR]: (subject: Subject) => {
    switch (subject) {
      case "Video":
        return { ...canManage };
      default:
        return false;
    }
  },
  // System Administrator
  [ROLES.SYSTEM_ADMIN]: (subject: Subject) => {
    switch (subject) {
      case "Administration":
        return { ...canManage };
      case "Video":
        return { ...canManage };
      default:
        return false;
    }
  },
};

export type Ability = "create" | "read" | "update" | "delete" | "manage";

const canManage = {
  manage: true,
  create: true,
  read: true,
  update: true,
  delete: true,
};

type AbilityInfo = {
  role: ROLES;
};

export class UserAbilities {
  config: AbilityInfo;

  constructor(config: AbilityInfo) {
    this.config = config;
  }

  can(
    ability: Ability | Ability[],
    subject: Subject,
    configOverrides?: Partial<AbilityInfo>,
  ): boolean {
    const config = { ...this.config, ...configOverrides };
    const roleAbilites = RoleAbilities[this.config.role];
    if (!roleAbilites) return false;
    const subjectAbilites = roleAbilites(subject, config);
    if (!subjectAbilites) return false;
    if (Array.isArray(ability))
      return ability.some((a) => Boolean(subjectAbilites[a]));
    return Boolean(subjectAbilites[ability]);
  }

  cannot(
    ability: Ability | Ability[],
    subject: Subject,
    configOverrides?: Partial<AbilityInfo>,
  ): boolean {
    return !this.can(ability, subject, configOverrides);
  }

  is(role: ROLES): boolean {
    return this.config.role === role;
  }
}

// Usage examples
// const ability = new UserAbilities({ role: ROLES.advanced_coordinator, isSiteAssigned: false })

// can("read", "GeneralCosts")
// can("read", "GeneralCosts", { isSiteAssigned: false })
// cannot("read", "GeneralCosts")

// Ability to read OR manage GeneralCosts
// can(["manage", "read"], "GeneralCosts")

// The user cannot manage OR read GeneralCosts
// cannot(["manage", "read"], "GeneralCosts")
